import {cleanEntity} from "@mytour/platform";
import type {EventReadResponse} from "@mytour/shared";
import {
  CustomerPageMetaSchema,
  PageHeaderBooleanSchema,
  PageHeaderCountSchema,
} from "@mytour/shared";
import type {
  CustomerDetail,
  CustomerListResponse,
  CustomerPage,
  CustomerPageParams,
  CustomerWriteResponse,
  ICustomer,
  IEvent,
} from "@mytour/shared";
import axios from "axios";
import {toCreateCustomerPayload} from "./customerCreatePayload.js";
import {toUpdateCustomerPayload} from "./customerUpdatePayload.js";

export interface GetCustomersParams {
  page?: number;
  size?: number;
  sort?: string;
}

export interface GetCustomersWithFilterParams {
  filter: string;
  page: number;
  size: number;
  sort?: string;
}

export interface GetCustomersResponse {
  data: ICustomer[];
  totalCount: number;
}

export const customerApiService = {
  async getCustomerPage(params: CustomerPageParams): Promise<CustomerPage> {
    const result = await axios.get<CustomerListResponse>("api/customers", {
      params,
    });
    return {
      data: result.data,
      meta: CustomerPageMetaSchema.parse({
        totalCount: PageHeaderCountSchema.parse(result.headers["x-total-count"]),
        hasCustomers: PageHeaderBooleanSchema.parse(result.headers["x-has-customers"]),
        searchPlaceholder: readSearchPlaceholder(result.headers["x-customer-search-placeholder"]),
      }),
    };
  },
  async getLastEventForCustomer(customerId: string): Promise<IEvent | null> {
    const result = await axios.get<EventReadResponse | null>(
      `api/events/last-for-customer/${customerId}`
    );
    return result.data;
  },

  async getCustomers(params: GetCustomersParams = {}): Promise<GetCustomersResponse> {
    const {page, size, sort} = params;
    const requestUrl = `api/customers${sort ? `?page=${page}&size=${size}&sort=${sort}` : ""}`;
    const result = await axios.get<CustomerListResponse>(
      `${requestUrl}${sort ? "&" : "?"}cacheBuster=${new Date().getTime()}`
    );
    return {
      data: result.data,
      totalCount: Number.parseInt(result.headers["x-total-count"] || "0", 10),
    };
  },

  async getCustomersNextPage(params: GetCustomersWithFilterParams): Promise<GetCustomersResponse> {
    const {filter, page, size, sort} = params;
    const requestUrl = `api/customers${
      sort ? `?filter=${filter}&page=${page}&size=${size}&sort=${sort}` : ""
    }`;
    const result = await axios.get<CustomerListResponse>(
      `${requestUrl}${sort ? "&" : "?"}cacheBuster=${new Date().getTime()}`
    );
    return {
      data: result.data,
      totalCount: Number.parseInt(result.headers["x-total-count"] || "0", 10),
    };
  },

  async refreshCustomers(params: {
    filter: string;
    size: number;
    sort?: string;
  }): Promise<GetCustomersResponse> {
    const {filter, size, sort} = params;
    const requestUrl = `api/customers${
      sort ? `?filter=${filter}&page=0&size=${size}&sort=${sort}` : ""
    }`;
    const result = await axios.get<CustomerListResponse>(
      `${requestUrl}${sort ? "&" : "?"}cacheBuster=${new Date().getTime()}`
    );
    return {
      data: result.data,
      totalCount: Number.parseInt(result.headers["x-total-count"] || "0", 10),
    };
  },

  async getCustomer(id: string): Promise<CustomerDetail> {
    const result = await axios.get<CustomerDetail>(`api/customers/${id}`);
    return result.data;
  },

  async createCustomer(customer: ICustomer): Promise<ICustomer> {
    const result = await axios.post<CustomerWriteResponse>(
      "api/customers",
      cleanEntity(toCreateCustomerPayload(customer))
    );
    return result.data;
  },

  async updateCustomer(customer: ICustomer): Promise<CustomerDetail> {
    const result = await axios.put<CustomerDetail>(
      "api/customers",
      cleanEntity(toUpdateCustomerPayload(customer))
    );
    return result.data;
  },

  async deleteCustomer(id: string): Promise<void> {
    await axios.delete(`api/customers/${id}`);
  },
};

function readSearchPlaceholder(value: unknown): string {
  if (typeof value !== "string") {
    return "Chercher par nom, ville ou notes";
  }
  try {
    return decodeURIComponent(value) || "Chercher par nom, ville ou notes";
  } catch {
    return "Chercher par nom, ville ou notes";
  }
}
